import { makeAutoObservable } from "mobx";
import { getRadarColormap } from "./RadarColormap";
import { DateTime } from "luxon";
import RootStore from "stores/root-store";
import { getEnv, getRoot } from "mobx-easy";
import i18n from "../../../i18n";
import { RootEnv } from "stores/setup/create-store";
import { ProductAnalyticsEvents } from "enum/ProductAnalyticsEvents";
import { StoreState } from "enum/StoreState";
import {
  RainbowMetadataResponse,
  PrecipitationFrame,
  DEFAULT_TILE_PARAMS,
  TIMELINE_CONSTANTS,
} from "../../../types/IRadarMapRainbow";

export class RadarMapStore {
  state: string = StoreState.DONE;
  currentIndex: number = 0;
  isPlaying: boolean = false;
  isNowcast: boolean = false;
  intervalId: NodeJS.Timeout | null = null;
  colormap = getRadarColormap();
  showRadar: boolean = false;
  isShowRadarClicked: boolean = false;
  isInitialLayer: boolean = true;

  // Rainbow AI properties
  metadata: RainbowMetadataResponse | null = null;
  frames: PrecipitationFrame[] = [];
  errorMessage: string | null = null;
  isLoadingMetadata: boolean = false;

  constructor() {
    makeAutoObservable(this);
  }

  setState(value: StoreState) {
    this.state = value;
  }

  reset() {
    this.setState(StoreState.DONE);
    this.currentIndex = 0;
    this.isPlaying = false;
    this.isNowcast = false;
    this.intervalId = null;
    this.showRadar = false;
    this.isShowRadarClicked = false;
    this.isInitialLayer = true;
    this.metadata = null;
    this.frames = [];
    this.errorMessage = null;
  }

  setIsNowcast(value: boolean) {
    this.isNowcast = value;
  }

  setIsPlaying(value: boolean) {
    this.isPlaying = value;
  }

  setIntervalId(value: NodeJS.Timeout | null) {
    this.intervalId = value;
  }

  setShowRadar(value: boolean) {
    localStorage.setItem("firstViewRadar", "true");
    this.setIsShowRadarClicked(true);
    this.showRadar = value;
    if (value && !this.isInitialLayer) {
      this.addProductAnalyticsEvent(
        ProductAnalyticsEvents.ForecastRadarLayerMapView,
        { environmental_variable: "precip" }
      );
    } else {
      this.setIsInitialLayer(false);
    }
  }

  setIsShowRadarClicked(value: boolean) {
    this.isShowRadarClicked = value;
  }

  setIsInitialLayer(value: boolean) {
    this.isInitialLayer = value;
  }

  incrementIndex() {
    this.currentIndex++;
  }

  decrementIndex() {
    this.currentIndex--;
  }

  resetIndex() {
    this.currentIndex = 0;
  }

  /**
   * Play radar animation for Rainbow AI 25-frame timeline.
   *
   * Cycles through frames 0-24 at specified interval (default: 1 second).
   * Loops back to frame 0 after reaching the last frame.
   *
   * @param intervalSeconds - Interval between frames in seconds (default: 1)
   */
  play(intervalSeconds = 1) {
    if (this.isPlaying) return;
    this.isPlaying = true;

    this.intervalId = setInterval(() => {
      const totalFrames = this.frames.length;

      if (totalFrames === 0) return;

      // Cycle through frames 0-24, loop back to 0
      if (this.currentIndex < totalFrames - 1) {
        this.incrementIndex();
        this.isNowcast = this.isNowcastFrame;
      } else {
        // Loop back to frame 0
        this.restart();
      }
    }, intervalSeconds * 1000);
  }

  stop() {
    this.setIsPlaying(false);
    if (this.intervalId) {
      clearInterval(this.intervalId);
      this.setIntervalId(null);
    }
  }

  restart() {
    this.resetIndex();
    this.setIsNowcast(false);
    this.play();
  }

  setCurrentIndex(index: number) {
    this.currentIndex = index;
    this.isNowcast = this.isNowcastFrame;
    this.stop();
  }

  get currentTimestamp() {
    const currentFrame = this.frames[this.currentIndex];
    return currentFrame?.timestamp || 0;
  }

  get elapsedTime() {
    const {
      dataStores: { userConfigStore },
    } = getRoot<RootStore>();

    if (!this.currentTimestamp) {
      return "";
    }

    const currentTime = DateTime.fromSeconds(this.currentTimestamp).setZone(
      userConfigStore.timezone
    );
    const now = DateTime.utc().setZone(userConfigStore.timezone);

    const timeString = currentTime.toFormat("H:mm'h'");
    const diffMinutes = Math.abs(
      Math.round(now.diff(currentTime, "minutes").minutes)
    );

    let timeDiffString;
    if (diffMinutes < 60) {
      timeDiffString =
        now > currentTime
          ? i18n.t("time.minutesAgo", { minutes: diffMinutes })
          : i18n.t("time.inMinutes", { minutes: diffMinutes });
    } else {
      const hours = Math.floor(diffMinutes / 60);
      const minutes = diffMinutes % 60;

      if (minutes === 0) {
        timeDiffString =
          now > currentTime
            ? i18n.t("time.hoursAgo", { hours })
            : i18n.t("time.inHours", { hours });
      } else {
        timeDiffString =
          now > currentTime
            ? i18n.t("time.hoursMinutesAgo", { hours, minutes })
            : i18n.t("time.inHoursMinutes", { hours, minutes });
      }
    }

    return `${timeString} - ${timeDiffString} `;
  }

  async addProductAnalyticsEvent(
    event: ProductAnalyticsEvents,
    eventProperty: Record<string, any>
  ) {
    const { logServices } = getEnv<RootEnv>();
    try {
      await logServices.addProductAnalyticsEvent(event, eventProperty);
    } catch (error) {
      console.error(`Error while call addProductAnalytics event`);
    }
  }

  // ===== Rainbow AI Integration Methods =====

  /**
   * Fetch Rainbow AI metadata from backend and build 25-frame timeline.
   *
   * On success: populates metadata and frames arrays.
   * On error: hides radar layer and sets errorMessage per FR-015.
   *
   * @example
   * await radarMapStore.fetchMetadata();
   */
  async fetchMetadata(): Promise<void> {
    const { radarService } = getEnv<RootEnv>();
    this.isLoadingMetadata = true;
    this.errorMessage = null;

    try {
      const response = await radarService.getRainbowMetadata();
      this.metadata = response;
      this.frames = this.buildFrames(response);
      this.currentIndex = TIMELINE_CONSTANTS.CURRENT_FRAME_INDEX;
    } catch (error: any) {
      console.error("Failed to fetch Rainbow AI metadata:", error);
      this.errorMessage = "radarLegend.error.unavailable";
      this.showRadar = false; // Hide radar on error per FR-015
    } finally {
      this.isLoadingMetadata = false;
    }
  }

  /**
   * Build 25-frame timeline from metadata.
   *
   * Timeline structure:
   * - Frames 0-11: Past observations (12 frames)
   * - Frame 12: Current snapshot (1 frame)
   * - Frames 13-24: Future forecasts (12 frames)
   *
   * @param metadata - Response from backend API
   * @returns Array of 25 PrecipitationFrame objects
   *
   * @private
   */
  private buildFrames(metadata: RainbowMetadataResponse): PrecipitationFrame[] {
    const frames: PrecipitationFrame[] = [];

    // Past frames (0-11): 12 historical snapshots
    metadata.past_snapshots.forEach((pastSnapshot, i) => {
      frames.push({
        index: i,
        type: "past",
        snapshot: pastSnapshot,
        forecast_time: 0,
        timestamp: pastSnapshot,
        tileUrlPattern: this.buildTileUrl(
          metadata.cloudfront_base_url,
          pastSnapshot,
          0
        ),
      });
    });

    // Current frame (12): snapshot with forecast_time=0
    frames.push({
      index: TIMELINE_CONSTANTS.CURRENT_FRAME_INDEX,
      type: "current",
      snapshot: metadata.snapshot,
      forecast_time: 0,
      timestamp: metadata.snapshot,
      tileUrlPattern: this.buildTileUrl(
        metadata.cloudfront_base_url,
        metadata.snapshot,
        0
      ),
    });

    // Future frames (13-24): 12 forecast frames
    // Skip forecast_time=0 (already used for current frame)
    metadata.forecast_times.slice(1).forEach((forecastTime, i) => {
      frames.push({
        index: TIMELINE_CONSTANTS.CURRENT_FRAME_INDEX + 1 + i,
        type: "future",
        snapshot: metadata.snapshot,
        forecast_time: forecastTime,
        timestamp: metadata.snapshot + forecastTime,
        tileUrlPattern: this.buildTileUrl(
          metadata.cloudfront_base_url,
          metadata.snapshot,
          forecastTime
        ),
      });
    });

    return frames;
  }

  /**
   * Build CloudFront tile URL pattern for a frame.
   *
   * URL format:
   * {cloudfront_base_url}/{snapshot}/{forecast_time}/{z}/{x}/{y}?color=0&coverage=1
   *
   * Placeholders {z}/{x}/{y} are filled by Leaflet tile layer.
   *
   * @param baseUrl - CloudFront distribution base URL
   * @param snapshot - Snapshot timestamp (Unix seconds)
   * @param forecastTime - Forecast offset in seconds (0 for past/current)
   * @returns Tile URL pattern with Leaflet placeholders
   *
   * @private
   * @example
   * buildTileUrl("https://cdn.example.com/tiles", 1701360000, 0)
   * // Returns: "https://cdn.example.com/tiles/1701360000/0/{z}/{x}/{y}?color=2&coverage=1"
   */
  private buildTileUrl(
    baseUrl: string,
    snapshot: number,
    forecastTime: number
  ): string {
    const { color, coverage } = DEFAULT_TILE_PARAMS;
    return `${baseUrl}/${snapshot}/${forecastTime}/{z}/{x}/{y}?color=${color}&coverage=${coverage}`;
  }

  /**
   * Get current frame in timeline.
   *
   * @returns Current frame or null if no frames loaded
   */
  get currentFrame(): PrecipitationFrame | null {
    return this.frames[this.currentIndex] ?? null;
  }

  /**
   * Get tile URL pattern for current frame.
   *
   * Used by RadarTileLayer to render tiles.
   *
   * @returns Tile URL pattern or undefined if no frame selected
   */
  get currentTileUrl(): string | undefined {
    return this.currentFrame?.tileUrlPattern;
  }

  /**
   * Check if current frame is a nowcast (future forecast).
   *
   * @returns true if current frame is forecast (index > 12), false otherwise
   */
  get isNowcastFrame(): boolean {
    return this.currentIndex > TIMELINE_CONSTANTS.CURRENT_FRAME_INDEX;
  }

  /**
   * Check if current frame is in the past.
   *
   * @returns true if current frame is historical (index < 12), false otherwise
   */
  get isPastFrame(): boolean {
    return this.currentIndex < TIMELINE_CONSTANTS.CURRENT_FRAME_INDEX;
  }

  /**
   * Check if current frame is the current snapshot.
   *
   * @returns true if current frame is current snapshot (index === 12), false otherwise
   */
  get isCurrentFrame(): boolean {
    return this.currentIndex === TIMELINE_CONSTANTS.CURRENT_FRAME_INDEX;
  }

  /**
   * Get timeline range with start and end timestamps.
   *
   * Returns the timestamp range spanning all 25 frames.
   * Used for timeline visualization and scrubbing.
   *
   * @returns Object with start (oldest) and end (newest) timestamps, or null if no frames
   * @example
   * const range = store.timelineRange;
   * // { start: 1701352800, end: 1701367200 } // 4-hour span
   */
  get timelineRange(): { start: number; end: number } | null {
    if (this.frames.length === 0) return null;

    return {
      start: this.frames[0].timestamp,
      end: this.frames[this.frames.length - 1].timestamp,
    };
  }

  /**
   * Get evenly-spaced time markers for timeline visualization.
   *
   * Calculates 5 time markers spread across the timeline range.
   * Used for displaying time labels on the timeline scrubber.
   *
   * @returns Array of 5 timestamps evenly distributed across timeline, or empty array if no frames
   * @example
   * const markers = store.timeMarkers;
   * // [1701352800, 1701356400, 1701360000, 1701363600, 1701367200]
   * // Each marker represents a time label position on the timeline
   */
  get timeMarkers(): number[] {
    const range = this.timelineRange;
    if (!range) return [];

    const { start, end } = range;
    const span = end - start;
    const step = span / 4; // 5 markers = 4 intervals

    return [start, start + step, start + step * 2, start + step * 3, end];
  }
}
